import { useCallback } from "react"
import type { CurrencyCode } from "@/schema/custom-field"

import { formatNumber, parseCurrencyInput, parseNumber } from "@/components/custom-fields/number-format"

import { InlineInput } from "./inline-input"
import { InlineInputProps } from "./props"

type NumberFormat = "currency" | "percentage" | "plain"

const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  USD: "$",
  EUR: "€",
  AUD: "$",
  CAD: "$",
}

interface InlineNumberInputProps extends Omit<InlineInputProps<string | null>, "parser" | "formatter"> {
  format?: NumberFormat
  currencyCode?: CurrencyCode
  allowNegative?: boolean
}

function formatCurrency(val: string | null, symbol: string = "$"): string {
  if (!val) return `${symbol}0`
  const num = parseInt(val)
  if (isNaN(num)) return `${symbol}0`
  const absFormatted = Math.abs(num).toLocaleString("en-US", { maximumFractionDigits: 0 })
  return num < 0 ? `-${symbol}${absFormatted}` : `${symbol}${absFormatted}`
}

function parseNumericChars(val: string): string | null {
  const cleaned = val.replace(/[^0-9.-]+/g, "")
  return cleaned || null
}

function formatPercentage(val: string | null): string {
  if (!val) return "0%"

  const num = parseFloat(val)
  if (isNaN(num)) return "0%"
  if (!val.includes(".")) return `${num}%`

  const [intStr, decStr = ""] = val.split(".")
  const sign = val.startsWith("-") ? "-" : ""

  return `${sign}${Math.abs(parseInt(intStr) || 0)}.${decStr.slice(0, 4)}%`
}

const buildFormatConfig = (symbol: string) => ({
  currency: {
    parser: parseNumericChars,
    formatter: (val: string | null) => formatCurrency(val, symbol),
    save: parseCurrencyInput,
    cursorMinPosition: symbol.length,
    cursorMaxOffset: undefined,
  },
  percentage: {
    parser: parseNumericChars,
    formatter: formatPercentage,
    save: (v: string) => formatNumber(v) || null,
    cursorMinPosition: undefined,
    cursorMaxOffset: 1,
  },
  plain: {
    parser: parseNumber,
    formatter: (val: string | null) => formatNumber(val) || "",
    save: (v: string) => formatNumber(v) || null,
    cursorMinPosition: undefined,
    cursorMaxOffset: undefined,
  },
})

export function InlineNumberInput({
  format = "plain",
  currencyCode,
  allowNegative = true,
  label,
  fixedLabelWidth,
  defaultValue,
  placeholder,
  isSaving,
  onSave,
  ...rest
}: InlineNumberInputProps) {
  const symbol = CURRENCY_SYMBOLS[currencyCode ?? "USD"]
  const config = buildFormatConfig(symbol)[format ?? "plain"]
  const isNegative = (val: string) => allowNegative && val.includes("-")
  const currencyCursorMin = (isNegative(defaultValue ?? "") ? 1 : 0) + symbol.length

  const parser = useCallback(
    (val: string) => {
      const parsed = config.parser(val)
      if (!allowNegative && parsed) return parsed.replace(/-/g, "")
      return parsed
    },
    [config, allowNegative]
  )

  const handleNegativeToggle = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, value: string, setValue: (v: string) => void): boolean => {
      if ((format !== "currency" && format !== "percentage") || e.key !== "-") return false

      if (!allowNegative) return true

      const numericValue = value.replace(/[^0-9.]/g, "")
      const currentlyNegative = value.includes("-")

      if (currentlyNegative) {
        setValue(numericValue)
      } else {
        setValue(`-${numericValue}`)
      }

      return true
    },
    [format, allowNegative]
  )

  return (
    <InlineInput
      key={defaultValue ?? ""}
      label={label}
      fixedLabelWidth={fixedLabelWidth}
      defaultValue={defaultValue ?? ""}
      placeholder={placeholder}
      isSaving={isSaving}
      onSave={(val) => onSave?.(config.save(String(val ?? "")))}
      parser={parser}
      formatter={currencyCode ? (val) => formatCurrency(val, symbol) : config.formatter}
      inputClasses={(val) => (isNegative(val) ? "text-red-accent" : undefined)}
      cursorMinPosition={format === "currency" ? currencyCursorMin : config.cursorMinPosition}
      cursorMaxOffset={config.cursorMaxOffset}
      onKeyDown={handleNegativeToggle}
      {...rest}
    />
  )
}
